import React, { useState, useEffect } from 'react';
import { serverAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

interface StarRatingProps {
    serverId: string;
    initialRating?: number;
    size?: 'sm' | 'md' | 'lg';
    onRatingChange?: (rating: number) => void;
}

const StarRating: React.FC<StarRatingProps> = ({
    serverId,
    initialRating = 0,
    size = 'md',
    onRatingChange,
}) => {
    const { isAuthenticated } = useAuth();
    const { t } = useLanguage();
    const [userRating, setUserRating] = useState<number | null>(null);
    const [hoveredRating, setHoveredRating] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ratingStats, setRatingStats] = useState<{ averageRating: number; totalRatings: number } | null>(null);

    const sizeClasses = {
        sm: 'h-5 w-5',
        md: 'h-7 w-7',
        lg: 'h-9 w-9',
    };

    // Fetch user's existing rating and stats on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch rating stats (public)
                const stats = await serverAPI.getRatingStats(serverId);
                setRatingStats(stats);

                // Fetch user's rating if authenticated
                if (isAuthenticated) {
                    const myRating = await serverAPI.getMyRating(serverId);
                    setUserRating(myRating.rating);
                }
            } catch (err) {
                console.error('Failed to fetch rating data:', err);
            }
        };

        fetchData();
    }, [serverId, isAuthenticated]);

    const handleStarClick = async (rating: number) => {
        if (!isAuthenticated) {
            setError(t('server.rating.login_required') || 'Please log in to rate servers');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await serverAPI.rateServer(serverId, rating);
            setUserRating(rating);

            // Refresh stats after rating
            const stats = await serverAPI.getRatingStats(serverId);
            setRatingStats(stats);

            if (onRatingChange) {
                onRatingChange(rating);
            }
        } catch (err) {
            console.error('Failed to rate server:', err);
            setError(t('server.rating.error') || 'Failed to submit rating');
        } finally {
            setIsLoading(false);
        }
    };

    const displayRating = hoveredRating ?? userRating ?? 0;

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
                {/* Interactive Stars */}
                <div
                    className={`flex gap-1 ${!isAuthenticated ? 'opacity-60' : ''}`}
                    onMouseLeave={() => setHoveredRating(null)}
                >
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            disabled={isLoading || !isAuthenticated}
                            className={`transition-transform ${isAuthenticated ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed'} disabled:opacity-50`}
                            onClick={() => handleStarClick(star)}
                            onMouseEnter={() => isAuthenticated && setHoveredRating(star)}
                            aria-label={`Rate ${star} stars`}
                        >
                            <svg
                                className={`${sizeClasses[size]} transition-colors ${star <= displayRating
                                        ? 'text-yellow-400'
                                        : 'text-gray-500'
                                    }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                        </button>
                    ))}
                </div>

                {/* Rating Info */}
                <div className="flex items-center gap-2 text-sm">
                    {ratingStats && (
                        <>
                            <span className="text-yellow-400 font-semibold">
                                {ratingStats.averageRating.toFixed(1)}
                            </span>
                            <span className="text-gray-500">
                                ({ratingStats.totalRatings} {ratingStats.totalRatings === 1
                                    ? (t('server.rating.vote') || 'vote')
                                    : (t('server.rating.votes') || 'votes')})
                            </span>
                        </>
                    )}
                </div>

                {/* Loading indicator */}
                {isLoading && (
                    <svg className="animate-spin h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                )}
            </div>

            {/* User Rating Status */}
            {userRating && (
                <p className="text-xs text-green-400">
                    {(t('server.rating.your_rating') || 'Your rating: {rating} stars').replace('{rating}', String(userRating))}
                </p>
            )}

            {/* Error message */}
            {error && (
                <p className="text-xs text-red-400">{error}</p>
            )}

            {/* Login hint */}
            {!isAuthenticated && (
                <p className="text-xs text-gray-500">
                    {t('server.rating.login_hint') || 'Sign in to rate this server'}
                </p>
            )}
        </div>
    );
};

export default StarRating;
